import React from "react";

// Slider field. Two modes:
//
//  1. Multi-bar (legacy "brand character" page): when the question has
//     options, each option is ONE bipolar slider —
//       option.value                    -> answer key
//       option.label / label_arabic     -> LEFT label
//       option.metadata.right_label /
//         metadata.right_label_arabic    -> RIGHT label
//     The answer is an object keyed by option.value -> numeric value, e.g.
//       { classic_contemporary: 50, heritage_modern: 30, ... }
//
//  2. Single (default): one bipolar slider driven by question.min_label /
//     max_label, or a plain numeric slider when those are absent.
const SingleSlider = ({ min, max, step, value, minLabel, maxLabel, lang, onChange }) => {
  const useTextLabels = !!(minLabel || maxLabel);
  const v =
    value === null || value === undefined || value === ""
      ? Math.round((min + max) / 2)
      : Number(value);
  return (
    <div className="dq-slider-row">
      <span
        style={{
          minWidth: 100,
          textAlign: lang === "ar" ? "left" : "right",
          fontWeight: useTextLabels ? 600 : 400,
        }}
      >
        {useTextLabels ? minLabel || min : min}
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={v}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ flex: 1 }}
      />
      <span
        style={{
          minWidth: 100,
          textAlign: lang === "ar" ? "right" : "left",
          fontWeight: useTextLabels ? 600 : 400,
        }}
      >
        {useTextLabels ? maxLabel || max : max}
      </span>
      {!useTextLabels && <span className="dq-slider-value">{v}</span>}
    </div>
  );
};

export const SliderField = ({ question, value, onChange, lang }) => {
  const min = question.min_value ?? 0;
  const max = question.max_value ?? 100;
  const step = question.step ?? 1;

  const bars = Array.isArray(question.options) ? question.options : [];

  // ---- Multi-bar mode (one option == one bar) ----------------------------
  if (bars.length) {
    const answers = value && typeof value === "object" && !Array.isArray(value) ? value : {};
    const setBar = (key, v) => onChange({ ...answers, [key]: v });

    return (
      <div className="dq-slider-bars">
        {bars.map((opt, i) => {
          const key = opt.value || `bar_${i}`;
          const meta = opt.metadata || {};
          const minLabel =
            (lang === "ar" && opt.label_arabic) || opt.label || "";
          const maxLabel =
            (lang === "ar" && meta.right_label_arabic) || meta.right_label || "";
          return (
            <div key={key} style={{ marginBottom: 14 }}>
              <SingleSlider
                min={min}
                max={max}
                step={step}
                value={answers[key]}
                minLabel={minLabel}
                maxLabel={maxLabel}
                lang={lang}
                onChange={(v) => setBar(key, v)}
              />
            </div>
          );
        })}
      </div>
    );
  }

  // ---- Single-slider mode ------------------------------------------------
  const minLabel =
    (lang === "ar" && question.min_label_arabic) || question.min_label || "";
  const maxLabel =
    (lang === "ar" && question.max_label_arabic) || question.max_label || "";

  return (
    <SingleSlider
      min={min}
      max={max}
      step={step}
      value={value}
      minLabel={minLabel}
      maxLabel={maxLabel}
      lang={lang}
      onChange={onChange}
    />
  );
};
